/**
 * Trial onboarding gate — pure decision logic (2026-08).
 *
 * Owner directive: a brand-new trial account must not wander off mid-setup.
 * Until the quick profile step is completed, every app surface (/desktop,
 * /wallet, chat, …) routes back to /onboarding, and signing out/in resumes
 * exactly where the user left off. The gate applies ONLY to active trial
 * subscriptions — activated accounts (client-free / paid / enterprise) and
 * expired trials are never redirected.
 *
 * Kept free of React/firebase imports so it unit-tests standalone; the live
 * wiring lives in useTrialOnboardingGate.ts + TrialOnboardingGate.tsx.
 */

import { isTrialSubscription, trialAwaitsCard, type TrialConversionView, TrialSubscriptionShape } from './trialStatus';

export interface OnboardingSessionLike {
  state?: string | null;
  steps?: { configCompleted?: boolean | null } | null;
  /** Staged trial flow position — written by Onboarding.tsx on every phase
   *  transition; 'done' only when the user actually launched the workspace. */
  trialPhase?: string | null;
}

/** Same doc shape the trial banner consumes (status + expiry included). */
export type GateSubscriptionShape = TrialSubscriptionShape;

function expiryMs(raw: unknown): number | null {
  if (raw == null) return null;
  const anyRaw = raw as any;
  try {
    if (typeof anyRaw?.toMillis === 'function') return anyRaw.toMillis();
    if (typeof anyRaw?.toDate === 'function') return anyRaw.toDate().getTime();
    if (typeof anyRaw?._seconds === 'number') return anyRaw._seconds * 1000;
    const t = new Date(anyRaw as string | number).getTime();
    return Number.isFinite(t) ? t : null;
  } catch {
    return null;
  }
}

/**
 * True when onboarding is actually FINISHED — not merely past step one.
 *
 * Hardened for task #56: saveTrialProfile writes state 'complete' at the end
 * of the FIRST staged phase (profile), so that marker alone opened the gate
 * two optional stages early — a user could abandon setup on the apps or team
 * stage and still roam the workspace. The staged flow's own field is the
 * truth: `trialPhase` is persisted on every transition and only becomes
 * 'done' when launchWorkspace runs. A session sitting on a LIVE stage
 * ('profile' | 'apps' | 'team') is incomplete regardless of the legacy
 * markers.
 *
 * Grandfathering: sessions with NO trialPhase field (finished before the
 * staged flow existed, or non-trial flows) keep the old markers, and the
 * retired 'orientation' phase counts as done — it was the post-setup recap,
 * so anyone parked there had already finished (Onboarding.tsx migrates those
 * to 'done' on next visit).
 *
 * A missing session doc counts as NOT complete — a trial_start redemption
 * that never reached /onboarding has no session yet, and that is exactly the
 * user the gate exists for.
 */
export function isOnboardingComplete(session: OnboardingSessionLike | null | undefined): boolean {
  const phase = session?.trialPhase;
  if (phase === 'profile' || phase === 'apps' || phase === 'team') return false;
  return session?.state === 'complete' || session?.steps?.configCompleted === true;
}

/**
 * Workspace context for the trial gate. The uid-keyed subscription doc only
 * describes the user's OWN (uid-keyed) workspace, so the gate must know which
 * workspace the user is actually in before it may redirect.
 */
export interface TrialGateContext {
  uid: string;
  /** Resolved org back-pointer (pickBackPointerAccountId), null when none. */
  activeMembershipAccountId: string | null;
  /** Pending-invite marker or just-joined-org window is live. */
  hasPendingInvite?: boolean;
}

/**
 * Core decision: does this signed-in user belong on /onboarding and nowhere
 * else right now?
 */
export function needsTrialOnboarding(
  subscription: GateSubscriptionShape | null | undefined,
  session: OnboardingSessionLike | null | undefined,
  nowMs: number,
  ctx?: TrialGateContext,
): boolean {
  if (!subscription) return false;
  // The trial gate owns ONLY the uid-keyed workspace's onboarding. A user
  // whose ACTIVE workspace is someone else's org (an invited member — whose
  // acceptance mints a native-workspace trial they never asked to set up) or
  // a created org must never be bounced into setting up their own workspace
  // (2026-08-19 owner report: invitee dropped into org profile setup).
  if (ctx?.activeMembershipAccountId && ctx.activeMembershipAccountId !== ctx.uid) return false;
  // Mid-acceptance the back-pointer write may still be in flight — the same
  // window needsAccountActivation already exempts. PendingInviteGate owns
  // the routing for these users.
  if (ctx?.hasPendingInvite) return false;
  // Activated accounts (is_client_free, paid, enterprise) are never gated —
  // same precedence the trial banner and the redemption classifier use.
  if (!isTrialSubscription(subscription)) return false;
  // Expired trials are handled by the trial-ended messaging, not onboarding.
  if (subscription.status && !['trialing', 'active'].includes(subscription.status)) return false;
  const exp = expiryMs(subscription.trial_expires_at);
  if (exp !== null && exp <= nowMs) return false;
  return !isOnboardingComplete(session);
}

/**
 * No-code lock (task #56, owner directive 2026-08-14: "it's the same for all
 * onboarding processes even with a new user that doesn't have a trial code").
 *
 * A brand-new signup with NO trial code lands with no subscription doc at
 * all — which used to mean NO gate, so they could wander into a broken,
 * account-less workspace. This is the affirmative test that locks that user
 * into /onboarding (whose no-billing branch shows activate/redeem).
 *
 * "No subscription doc" alone is NOT enough to gate on — that condition is
 * also true for populations that must never see /onboarding:
 *  - invited org members (their signal is the users/{uid}/account_membership
 *    back-pointer; rbacAcceptInvitation never writes a subscription doc);
 *  - OWNERS of UID-keyed accounts (INCIDENT 2026-08-14: Fernando's activated
 *    account was forced into onboarding — the resolver's Step-2 fallback
 *    resolves owners via accounts/{uid}/profile/main with NO back-pointer
 *    doc, so "no membership" never means "no organization");
 *  - internal staff + admin-claim accounts (src/config/internalAccess.ts);
 *  - an invitee mid-acceptance (pending-invite marker / just-joined window —
 *    PendingInviteGate owns their routing, and right after acceptance the
 *    back-pointer write may still be in flight);
 *  - anyone whose subscription/membership READ failed (fail-open: a rules
 *    hiccup must never trap a paying member on a screen they can't complete);
 *  - legacy accounts with a completed onboarding session;
 *  - EVERY account created before this gate shipped (same incident): doc
 *    shapes older than the gate are unknowable, so pre-epoch accounts are
 *    grandfathered absolutely — this lock is for NEW signups only, per the
 *    owner directive ("a new user that doesn't have a trial code").
 * Every one of those stands down here; only the genuinely brand-new,
 * unactivated, unaffiliated signup is gated.
 */
export const ACTIVATION_GATE_EPOCH_MS = Date.parse('2026-08-14T00:00:00Z');

export interface ActivationGateInput {
  /** users/{uid}/subscription/subscription doc, null when it doesn't exist. */
  subscription: GateSubscriptionShape | null | undefined;
  subscriptionReadFailed?: boolean;
  /** Resolved org back-pointer (pickBackPointerAccountId), null when none. */
  membershipAccountId: string | null;
  membershipReadFailed?: boolean;
  /** accounts/{uid}/profile/main exists — the UID-keyed OWNER fallback. */
  ownerAccountDocExists?: boolean;
  session: OnboardingSessionLike | null | undefined;
  isInternalOrAdmin?: boolean;
  /** Pending-invite marker or just-joined-org window is live. */
  hasPendingInvite?: boolean;
  /** Firebase auth account creation time (user.metadata), null if unknown. */
  accountCreatedAtMs?: number | null;
}

export function needsAccountActivation(input: ActivationGateInput): boolean {
  if (input.subscription) return false; // any subscription doc → the trial gate owns it
  if (input.subscriptionReadFailed || input.membershipReadFailed) return false;
  if (input.membershipAccountId) return false;
  if (input.ownerAccountDocExists) return false;
  if (input.isInternalOrAdmin) return false;
  if (input.hasPendingInvite) return false;
  // Pre-epoch (and unknown-age) accounts are grandfathered absolutely.
  if (typeof input.accountCreatedAtMs !== 'number' || !(input.accountCreatedAtMs >= ACTIVATION_GATE_EPOCH_MS)) {
    return false;
  }
  return !isOnboardingComplete(input.session);
}

/**
 * App surfaces the gate bounces back to /onboarding. Marketing/legal pages
 * stay reachable — the lock applies to the product, not the website.
 * Matching is prefix-based on a lowercase pathname ('/wallet' also covers
 * '/wallet/anything').
 */
const GATED_PATH_PREFIXES = [
  '/desktop',
  '/wallet',
  '/activity',
  '/integrations',
  '/notifications',
  '/settings',
  '/subscription',
  '/infrastructure',
  '/profile',
  '/preview',
  '/portal-controls',
  '/admin',
  '/dashboard',
];

/**
 * The two pages a gate sends people TO, so never sends them away from: onboarding itself, and the trial's card step
 * (/subscription/trial, 2026-10-20) — it lives under /subscription, which is gated, and is where a required trial's
 * card is added, onboarding's last step.
 */
const GATE_DESTINATIONS = ['/onboarding', '/subscription/trial'];

export function isGatedAppPath(pathname: string): boolean {
  // Router matching is case-insensitive (v6 default) — mirror it here so
  // '/Wallet' can't slip past the gate.
  const path = (pathname || '').toLowerCase();
  if (GATE_DESTINATIONS.some((p) => path === p || path.startsWith(`${p}/`))) return false;
  return GATED_PATH_PREFIXES.some(
    (p) => path === p || path.startsWith(`${p}/`),
  );
}

/**
 * THE CARD STEP (docs/PLAN-trial-card-on-file-20261020.md; Miles, 2026-10-20): a trial that needs its card before any
 * work sends the person who can add it — the workspace's owner or a billing manager — from every app page to
 * /subscription/trial, where the card is added and the trial's clock starts. Nobody else is sent: an invited member
 * meets the server's member-aware refusal in chat (who to ask), never a card field (invitations stay easy). The
 * onboarding gate goes first: someone who still owes onboarding finishes it, and its last step is this one.
 *
 * Fail-open, like the onboarding gate: until the trial and the person's billing permission have both been read, no
 * one is sent anywhere, and a read that failed reads as "no card needed" (the server's gate still holds the work).
 */
export interface TrialCardGateInput {
  conversion: TrialConversionView | null;
  /** The trial status has been read (not loading). */
  trialReady: boolean;
  /** The person's billing permission has been read. */
  permissionReady: boolean;
  /** The owner, or a holder of billing.manage, in the workspace that is open. */
  canManageBilling: boolean;
}

export function needsTrialCard(input: TrialCardGateInput): boolean {
  if (!input.trialReady || !input.permissionReady) return false;
  return input.canManageBilling && trialAwaitsCard(input.conversion);
}

/** Where the card step is, carrying where the person was going, so they land there once the card is in. */
export function trialCardPath(fromPath: string, fromSearch = ''): string {
  const back = `${fromPath || ''}${fromSearch || ''}`;
  return back && back !== '/' ? `/subscription/trial?return=${encodeURIComponent(back)}` : '/subscription/trial';
}

/**
 * NOTE (2026-08-14, incident follow-up): '/' is deliberately NOT part of the
 * redirect perimeter. A first cut of task #56 redirected the home page too
 * ("the chat front door is the workspace") and the owner immediately hit it
 * on mobile — going home kept bouncing into onboarding. Users must be able
 * to reach the home page without being forced anywhere; the workspace lock
 * on '/' is enforced by HIDING the chat widget while onboarding is owed
 * (AppGlobalChat), never by hijacking the route.
 */
